import { formatCents } from "../../../../lib/sales";
import type { useT } from "../../../../i18n/I18nProvider";

type T = ReturnType<typeof useT>;

/** One quiet line of the totals block. */
function Line({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-6">
      <span className={`text-[12.5px]${muted ? " crm-mut" : ""}`}>{label}</span>
      <span className={`crm-tnum text-[13px]${muted ? " crm-mut" : ""}`}>{value}</span>
    </div>
  );
}

/**
 * The money at the foot of a document: subtotal, VAT, total, and - once
 * something has been paid against it - what is still owed.
 *
 * Every figure is `crm-tnum`, so the digits sit in a column and the eye can
 * compare them without reading. The total is the only bold line: it is the one
 * number the reader came for, and bolding all four would mean bolding none.
 *
 * With no VAT the subtotal goes too. It would be the total, printed a second
 * time under a different name, and two identical figures invite the reader to
 * hunt for the difference between them. The caller decides whether VAT applies
 * (`@wa/core/sales-vat`); this block only draws the answer.
 */
export function DocTotals({
  t,
  showVat = true,
  subtotalCents,
  taxCents,
  totalCents,
  paidCents,
}: {
  t: T;
  showVat?: boolean;
  subtotalCents: number;
  taxCents: number;
  totalCents: number;
  paidCents?: number;
}) {
  return (
    <div className="min-w-[224px] space-y-1.5">
      {showVat ? (
        <>
          <Line label={t("sales.totals.subtotal")} value={formatCents(subtotalCents)} />
          <Line label={t("sales.totals.tax")} value={formatCents(taxCents)} />
        </>
      ) : null}

      <div className="flex items-center justify-between gap-6 border-t border-[var(--crm-hair)] pt-2.5">
        <span className="text-[13px] font-bold">{t("sales.totals.total")}</span>
        <span className="crm-tnum text-[15px] font-bold">{formatCents(totalCents)}</span>
      </div>

      {paidCents !== undefined && paidCents > 0 ? (
        <>
          <Line label={t("sales.totals.paid")} value={formatCents(paidCents)} muted />
          {/* Amber, not red: money that is still owed is not yet money that is
              late. Only the overdue status says late, and it says it in the
              status pill. */}
          <div className="flex items-center justify-between gap-6 text-amber-600">
            <span className="text-[13px] font-bold">{t("sales.totals.due")}</span>
            <span className="crm-tnum text-[13.25px] font-bold">{formatCents(totalCents - paidCents)}</span>
          </div>
        </>
      ) : null}
    </div>
  );
}
